/** Phones: the form's dots closing back into the menu button (Menu.tsx).
 *  travelDots: the thank-you's cross. travelArrow: the send arrow, when the
 *  form's left unsent (a tap on the blur): its three dots turn into the
 *  diamond's top, right and bottom, the lower one splitting off the left.
 *
 *  The cross: Its five
 *  dots travel, each in its own time and along its own curve, to the button's
 *  four (a diamond, ASSETS.menu: 3px dots, 7px from its middle): each corner
 *  turns a quarter on round the middle into the diamond's next point; the
 *  middle one drops into the bottom one and is gone. Meanwhile the real icon
 *  is hidden (html .nto27[data-dots-travel], contact.css), and shown again,
 *  in place, when they're there. */

const EASE = "cubic-bezier(.65,0,.2,1)"
const STEPS = 24
// (each corner: its delay, how long it goes, how far it swings out on its way)
const CORNERS: Record<string, { delay: number; ms: number; swing: number }> = {
    tl: { delay: 0, ms: 760, swing: 5 },
    br: { delay: 70, ms: 720, swing: 4 },
    tr: { delay: 150, ms: 800, swing: 6 },
    bl: { delay: 210, ms: 740, swing: 4.5 },
}

export function travelDots(cross: HTMLElement, button: HTMLElement, root: HTMLElement) {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const dots = [...cross.querySelectorAll<HTMLElement>(".ct-dots i")]
    if (dots.length !== 5) return
    const c = cross.getBoundingClientRect()
    const from = { x: c.left + c.width / 2, y: c.top + c.height / 2 }
    const b = button.getBoundingClientRect()
    const to = { x: b.left + b.width / 2, y: b.top + b.height / 2 }
    const size0 = dots[0].getBoundingClientRect().width || 3.9

    const layer = document.createElement("div")
    layer.className = "ct-travel"
    document.body.appendChild(layer)
    root.setAttribute("data-dots-travel", "")

    let end = 0
    const done: Promise<unknown>[] = []
    const dot = () => {
        const d = document.createElement("i")
        d.style.width = d.style.height = size0 + "px"
        layer.appendChild(d)
        return d
    }
    // (the colour: white on the black bar, black on the white button, turning as the button does)
    const tone = (d: HTMLElement) =>
        d.animate([{ backgroundColor: "#fff" }, { backgroundColor: "#000" }], { duration: 600, delay: 150, easing: "cubic-bezier(.85,0,.15,1)", fill: "both" })

    for (const el of dots) {
        const r = el.getBoundingClientRect()
        const p = { x: r.left + r.width / 2 - from.x, y: r.top + r.height / 2 - from.y }
        const d = dot()
        if (Math.hypot(p.x, p.y) < 2) {
            // the middle one: a small swing aside, into the bottom point, gone
            const frames: Keyframe[] = []
            for (let k = 0; k <= STEPS; k++) {
                const e = k / STEPS
                const x = from.x + (to.x - from.x) * e + Math.sin(Math.PI * e) * 5
                const y = from.y + (to.y + 7 - from.y) * e
                const s = size0 + (3 - size0) * e
                frames.push({ transform: `translate(${x - s / 2}px, ${y - s / 2}px)`, width: s + "px", height: s + "px", opacity: e > 0.85 ? 0 : 1 })
            }
            const delay = 120
            done.push(d.animate(frames, { duration: 640, delay, easing: EASE, fill: "both" }).finished)
            tone(d)
            end = Math.max(end, delay + 640)
            continue
        }
        const key = (p.y < 0 ? "t" : "b") + (p.x < 0 ? "l" : "r")
        const { delay, ms, swing } = CORNERS[key]
        const a0 = Math.atan2(p.y, p.x),
            r0 = Math.hypot(p.x, p.y)
        const frames: Keyframe[] = []
        for (let k = 0; k <= STEPS; k++) {
            const e = k / STEPS
            // a quarter turn on (clockwise on screen), in to 7px, swinging out on the way
            const a = a0 + (Math.PI / 4) * e
            const rr = r0 + (7 - r0) * e + Math.sin(Math.PI * e) * swing
            const cx = from.x + (to.x - from.x) * e,
                cy = from.y + (to.y - from.y) * e
            const s = size0 + (3 - size0) * e
            frames.push({ transform: `translate(${cx + Math.cos(a) * rr - s / 2}px, ${cy + Math.sin(a) * rr - s / 2}px)`, width: s + "px", height: s + "px" })
        }
        done.push(d.animate(frames, { duration: ms, delay, easing: EASE, fill: "both" }).finished)
        tone(d)
        end = Math.max(end, delay + ms)
    }
    // there: the button's own icon again, where they are
    const finish = () => {
        root.removeAttribute("data-dots-travel")
        layer.remove()
    }
    Promise.all(done).then(finish, finish)
    setTimeout(finish, end + 400)
}

/** the send arrow's three dots into the button's four: the top one turns up
 *  to the top point, the right one out to the right, the lower one down to the
 *  bottom, and a fourth splits off it round to the left; each in its own time */
export function travelArrow(arrow: HTMLElement, button: HTMLElement, root: HTMLElement) {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const dots = [...arrow.querySelectorAll<HTMLElement>(".ct-dots i")]
    if (dots.length !== 3) return
    const c = arrow.getBoundingClientRect()
    const from = { x: c.left + c.width / 2, y: c.top + c.height / 2 }
    const b = button.getBoundingClientRect()
    const to = { x: b.left + b.width / 2, y: b.top + b.height / 2 }
    const size0 = dots[0].getBoundingClientRect().width || 3.9

    const layer = document.createElement("div")
    layer.className = "ct-travel"
    document.body.appendChild(layer)
    root.setAttribute("data-dots-travel", "")

    const done: Promise<unknown>[] = []
    let end = 0
    const go = (p: { x: number; y: number }, a1: number, delay: number, ms: number, swing: number) => {
        const d = document.createElement("i")
        d.style.width = d.style.height = size0 + "px"
        layer.appendChild(d)
        const a0 = Math.atan2(p.y, p.x),
            r0 = Math.hypot(p.x, p.y)
        const frames: Keyframe[] = []
        for (let k = 0; k <= STEPS; k++) {
            const e = k / STEPS
            const a = a0 + (a1 - a0) * e
            const rr = r0 + (7 - r0) * e + Math.sin(Math.PI * e) * swing
            const cx = from.x + (to.x - from.x) * e,
                cy = from.y + (to.y - from.y) * e
            const s = size0 + (3 - size0) * e
            frames.push({ transform: `translate(${cx + Math.cos(a) * rr - s / 2}px, ${cy + Math.sin(a) * rr - s / 2}px)`, width: s + "px", height: s + "px" })
        }
        done.push(d.animate(frames, { duration: ms, delay, easing: EASE, fill: "both" }).finished)
        d.animate([{ backgroundColor: "#fff" }, { backgroundColor: "#000" }], { duration: 600, delay: 150, easing: "cubic-bezier(.85,0,.15,1)", fill: "both" })
        end = Math.max(end, delay + ms)
    }
    const Q = Math.PI / 2
    for (const el of dots) {
        const r = el.getBoundingClientRect()
        const p = { x: r.left + r.width / 2 - from.x, y: r.top + r.height / 2 - from.y }
        if (p.x > 0) go(p, 0, 90, 720, 3) // the point: out to the right
        else if (p.y < 0) go(p, -Q, 0, 760, 4) // the upper: up to the top
        else {
            go(p, Q, 160, 740, 3.5) // the lower: down to the bottom
            go(p, Math.PI, 230, 800, 5.5) // and, split off it, round to the left
        }
    }
    const finish = () => {
        root.removeAttribute("data-dots-travel")
        layer.remove()
    }
    Promise.all(done).then(finish, finish)
    setTimeout(finish, end + 400)
}
